import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, switchMap } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { EventService } from '../event/event.service';
import { EncryptionMigrator, EncryptionService } from '@evorait/pace-encryption';
import { UserMasterService } from '../user-master/user-master.service';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { HttpClient } from '@angular/common/http';
import { EncryptionResponse, EncryptionStatus } from './encryption.interface';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { EncryptionKeyModel, ODataEncryptionKeyModel } from '../../models/low-level/encryption-key.model';
import CryptoJS from 'crypto-js';
import { AppId } from '../client-manager/client-manager.service';
import { EnvironmentService } from '@evorait/pace-environment';

@Injectable({
    providedIn: 'root',
})
export class EncryptionInitializer {
    public isInitialized = false;
    public constructor(
        private readonly onlineOnlyService: OnlineOnlyService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly eventService: EventService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly encryptionService: EncryptionService,
        private readonly userMasterService: UserMasterService,
        private readonly environmentService: EnvironmentService,
        private readonly http: HttpClient,
        private readonly encryptionMigrator: EncryptionMigrator,
    ) {}

    public init(): Observable<void> {
        if (
            !this.userMasterService.isPaceOnlineUser() &&
            !this.clientManager.isPaceFormsFI() &&
            !this.clientManager.isPacePWA()
        ) {
            this.isInitialized = true;
            return of(undefined);
        }
        return this.getUserEncryptedKey().pipe(
            switchMap((encryptionKey) => this.encryptionService.init(encryptionKey)),
            tap(() => {
                this.isInitialized = this.encryptionService.isInitialized;
                this.eventService.triggerEncryptionInitialized();
            }),
        );
    }

    private getUserEncryptedKey(): Observable<string> {
        // the environment should be properly refactored. this URL should point to the azure proxy
        const hostUrl = this.environmentService.apiBase;
        const uriEncryption = ENDPOINTS.EncryptionKey;

        return this.http.get<EncryptionResponse>(`${hostUrl}${uriEncryption}`).pipe(
            map((response) => {
                if (response.state === EncryptionStatus.Error) {
                    this.log.error(
                        this.constructor.name,
                        'getUserEncryptedKey: Error while fetching encryption key',
                        response.error,
                    );
                    throw new Error(response.error);
                }

                return atob(response.dek);
            }),
        );
    }

    /**
     *
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns legacy key from GW
     */
    public retrieveLegacyKey(): Observable<string> {
        return this.onlineOnlyService.get(ENDPOINTS.LegacyEncryptionKey).pipe(
            map((encryptionResponse: ODataEncryptionKeyModel[]) => {
                if (!encryptionResponse || encryptionResponse.length === 0) {
                    this.log.error(`${this.constructor.name}: retrieveLegacyKey: encryptionResponse is empty`);
                    throw new Error(`${this.constructor.name}: retrieveLegacyKey: failed to get valid encryption key`);
                }
                const encryptionKeyModels = encryptionResponse[0];
                return this.extractLegacyKeyFromModel(
                    new EncryptionKeyModel(encryptionKeyModels.UserName, encryptionKeyModels.EncryptedKey),
                );
            }),
            take(1),
        );
    }

    /**
     *
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @param legacyKeyModel the encryption key model, as obtained from GW
     * @returns the key extracted from the key model
     */
    private extractLegacyKeyFromModel(legacyKeyModel: EncryptionKeyModel): string {
        const encryptedPassphrase = legacyKeyModel.encryptedKey;
        if (encryptedPassphrase) {
            // The passphrase from GW is encrypted. We need to decrypt it in order to use it.
            const encryptionKey = CryptoJS.AES.decrypt(encryptedPassphrase, AppId.paceonline).toString(
                CryptoJS.enc.Utf8,
            );
            if (encryptionKey.startsWith(EncryptionMigrator.PREFIX_LEGACY_ENCRYPTED_KEY)) {
                return encryptionKey;
            }
            // The encryption of the passphrase did not work.
            // Without a passphrase any encryption or decryption will not work.
            this.log.error(
                `EncryptionMigratorService: extractOldKeyFromModel: The decryption of the passphrase did not work!`,
            );
        } else {
            // Without a passphrase any encryption or decryption will not work.
            this.log.error(
                `EncryptionMigratorService: extractOldKeyFromModel: Without a passphrase any encryption or decryption will not work!`,
            );
        }
        throw new Error(`EncryptionMigratorService: extractOldKeyFromModel: failed to extract old encryption key!`);
    }
}
